import { isHabitatScoped, type ProductPromptType } from '@/components/products/ProductsPromptModal/redux/types';

import type { ProductPair, ProductPairInputs } from '../types';
import { isActive } from './entitlement';

/**
 * Which pair of products the visitor is offered.
 *
 * A pure function of `ppt`, the habitat from the route, and the visitor's state — and nothing
 * else. In particular `utm_source` is absent by construction, which is the whole of ADR 0004:
 * marketing attribution must never change what is on sale, or the offer matrix stops being
 * enumerable.
 *
 * This is the single implementation. `getDefaultProductsPromptModalState` in the products prompt
 * modal delegates here rather than keeping a second copy.
 */

/** The history-based default, used when no `ppt` names a specific offer. */
const defaultPair = (habitatId: string | undefined, user: ProductPairInputs, now: number): ProductPair => {
  const { persona, featuresUntil, habitatsAccessUntil, dayPassHabitatsHistory, supportedHabitatsHistory } = user;
  const isEducator = persona === 'educator';
  const isExplorerFeatureExpired = !!featuresUntil?.explorer && !isActive(featuresUntil, 'explorer', now);

  if (!habitatId) {
    return {
      primaryProductType: isEducator ? 'educator-week-pass' : 'all-habitats-day-pass',
      secondaryProductType: isExplorerFeatureExpired ? 'explorer-pass' : undefined,
      forEducator: isEducator,
    };
  }

  // Holding a live day pass or sponsorship anywhere means the upgrade, not another day pass.
  const hasActiveHabitatAccess = Object.values(habitatsAccessUntil ?? {}).some((value) => Date.parse(value) > now);
  if (hasActiveHabitatAccess || isExplorerFeatureExpired) {
    return isEducator
      ? { habitatId, primaryProductType: 'educator-pass', secondaryProductType: 'explorer-pass', forEducator: true }
      : {
          habitatId,
          primaryProductType: hasActiveHabitatAccess ? 'explorer-pass' : 'all-habitats-day-pass',
          secondaryProductType: hasActiveHabitatAccess ? 'single-habitat-day-pass' : 'explorer-pass',
        };
  }

  // Somewhere they have been before by day pass or sponsorship (L1a/L2): offer to sponsor it.
  if (!isEducator && (dayPassHabitatsHistory.has(habitatId) || supportedHabitatsHistory.has(habitatId))) {
    return { habitatId, primaryProductType: 'sponsorship', secondaryProductType: 'single-habitat-day-pass' };
  }

  return {
    habitatId,
    primaryProductType: isEducator ? 'educator-week-pass' : 'all-habitats-day-pass',
    secondaryProductType: 'single-habitat-day-pass',
    forEducator: isEducator,
  };
};

const sponsorshipPair = (habitatId: string | undefined, user: ProductPairInputs, now: number): ProductPair => {
  const hasExplorerFeature = user.isSpecialRole || isActive(user.featuresUntil, 'explorer', now);
  const hasAccess = !!habitatId && isActive(user.habitatsAccessUntil, habitatId, now);
  const supportedBefore = !!habitatId && user.supportedHabitatsHistory.has(habitatId);

  return {
    habitatId,
    primaryProductType: 'sponsorship',
    secondaryProductType: hasExplorerFeature
      ? undefined
      : supportedBefore && !hasAccess
      ? 'single-habitat-day-pass'
      : 'explorer-pass',
  };
};

/**
 * Whether a pair would put nothing on screen.
 *
 * A habitat-scoped `ppt` can arrive on a page that has no habitat — a campaign link to a lesson
 * carrying `?ppt=single-habitat-day-pass`, say. Every card in the pair is then dropped for want of
 * a habitat to name, and the visitor is shown a step offering nothing at all. Falling back to what
 * their own history names is worse than the offer they asked for and far better than an empty
 * modal.
 */
const rendersNothing = ({ habitatId, primaryProductType, secondaryProductType }: ProductPair) =>
  !habitatId && [primaryProductType, secondaryProductType].every((type) => !type || isHabitatScoped(type));

const pairForPromptType = (
  ppt: ProductPromptType | undefined,
  habitatId: string | undefined,
  user: ProductPairInputs,
  now: number,
): ProductPair => {
  switch (ppt) {
    case 'explorer':
      return { primaryProductType: 'explorer-pass' };
    case 'educator':
      return { primaryProductType: 'educator-pass', secondaryProductType: 'educator-week-pass' };
    case 'premium':
      return { primaryProductType: 'explorer-pass', secondaryProductType: 'educator-pass' };
    case 'all-access':
      return { primaryProductType: 'all-habitats-day-pass' };
    case 'day-pass':
      return {
        habitatId,
        primaryProductType: 'all-habitats-day-pass',
        secondaryProductType: 'single-habitat-day-pass',
      };
    case 'single-habitat-day-pass':
      return { habitatId, primaryProductType: 'single-habitat-day-pass' };
    case 'sponsor':
      return sponsorshipPair(habitatId, user, now);
    default:
      return defaultPair(habitatId, user, now);
  }
};

const selectProductPair = (
  ppt: ProductPromptType | undefined,
  habitatId: string | undefined,
  user: ProductPairInputs,
  now: number = Date.now(),
): ProductPair => {
  const pair = pairForPromptType(ppt, habitatId, user, now);
  return rendersNothing(pair) ? defaultPair(habitatId, user, now) : pair;
};

export default selectProductPair;
